import LinkIcon from "@material-ui/icons/Link";
import React, { useMemo, useState } from "react";
import {
  Button,
  Create,
  Datagrid,
  DateField,
  DateTimeInput,
  FunctionField,
  List,
  NumberInput,
  required,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  useCreate,
  useDelete,
  useNotify,
  useRecordContext,
  useRefresh,
  useUpdate,
} from "react-admin";

const SERVICE_CHOICES = [
  "billing",
  "wordpress",
  "wedding",
  "story",
  "sharers",
  "schedule",
  "notification",
  "dnd_form",
  "stock_analysis",
].map((id) => ({ id, name: id.replace(/_/g, " ") }));

const activationBaseUrl = () => {
  if (window.location.hostname === "localhost") return "http://localhost:3000";
  return window.location.hostname.includes("develop")
    ? "https://app-develop.sallyer.com"
    : "https://app.sallyer.com";
};

const RevokeButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading }] = useUpdate();

  if (!record || record.status !== "active") return null;

  const revoke = () => {
    update(
      "service-activations",
      { id: record.id, data: { status: "revoked" }, previousData: record },
      {
        onSuccess: () => {
          notify("Activation link revoked", { type: "success" });
          refresh();
        },
        onError: () =>
          notify("Could not revoke activation link", { type: "error" }),
      }
    );
  };

  return (
    <Button
      label="Revoke"
      color="secondary"
      onClick={revoke}
      disabled={isLoading}
    />
  );
};

const DeleteButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [deleteOne, { isLoading }] = useDelete();
  const canDelete =
    record &&
    ["expired", "revoked"].includes(record.status) &&
    record.usedCount === 0 &&
    record.redeemerCount === 0;

  if (!canDelete) return null;

  const remove = () => {
    if (!window.confirm("Permanently delete this unused activation record?"))
      return;
    deleteOne(
      "service-activations",
      { id: record.id, previousData: record },
      {
        onSuccess: () => {
          notify("Unused activation record deleted", { type: "success" });
          refresh();
        },
        onError: () =>
          notify("Only expired or revoked unused links can be deleted", {
            type: "error",
          }),
      }
    );
  };

  return (
    <Button
      label="Delete"
      color="secondary"
      onClick={remove}
      disabled={isLoading}
    />
  );
};

export const ServiceActivationList = () => (
  <>
    <p style={{ margin: "1rem 1.5rem 0", color: "#555" }}>
      Revoke disables future redemptions but keeps the record for audit and
      support. Expired or revoked links can be deleted only when nobody has
      redeemed them.
    </p>
    <List sort={{ field: "createdAt", order: "DESC" }}>
      <Datagrid bulkActionButtons={false}>
        <TextField source="serviceKey" />
        <TextField source="status" />
        <FunctionField
          label="Uses"
          render={(record: any) => `${record.usedCount} / ${record.maxUses}`}
        />
        <TextField source="campaign" emptyText="—" />
        <TextField source="note" emptyText="—" />
        <DateField source="expiresAt" showTime />
        <DateField source="createdAt" showTime />
        <RevokeButton />
        <DeleteButton />
      </Datagrid>
    </List>
  </>
);

export const ServiceActivationCreate = () => {
  const [create, { isLoading }] = useCreate();
  const notify = useNotify();
  const [activationUrl, setActivationUrl] = useState("");
  const defaultExpiry = useMemo(() => {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + 7);
    const localExpiry = new Date(
      expiry.getTime() - expiry.getTimezoneOffset() * 60_000
    );
    return localExpiry.toISOString().slice(0, 16);
  }, []);

  const submit = (data: any) => {
    const payload = {
      ...data,
      expiresAt: new Date(data.expiresAt).toISOString(),
    };
    create(
      "service-activations",
      { data: payload },
      {
        onSuccess: (result: any) => {
          const url = `${activationBaseUrl()}/activate/${result.code}`;
          setActivationUrl(url);
          notify("Activation link created. Copy it before leaving this page.", {
            type: "success",
          });
        },
        onError: () =>
          notify("Could not create activation link", { type: "error" }),
      }
    );
  };

  const copy = async () => {
    await navigator.clipboard.writeText(activationUrl);
    notify("Activation link copied", { type: "success" });
  };

  return (
    <Create redirect={false}>
      {activationUrl ? (
        <div style={{ padding: "2rem", maxWidth: 760 }}>
          <h2>Activation link created</h2>
          <p>This secret is shown once. Copy it before leaving this page.</p>
          <input
            aria-label="Activation URL"
            value={activationUrl}
            readOnly
            style={{ width: "100%", padding: "0.75rem", marginBottom: "1rem" }}
          />
          <Button label="Copy activation link" onClick={copy}>
            <LinkIcon />
          </Button>
        </div>
      ) : (
        <SimpleForm onSubmit={submit}>
          <SelectInput
            source="serviceKey"
            label="Service"
            choices={SERVICE_CHOICES}
            validate={required()}
            helperText="The product this link grants. The server decides the destination after redemption."
            fullWidth
          />
          <DateTimeInput
            source="expiresAt"
            label="Expires at"
            defaultValue={defaultExpiry}
            validate={required()}
            helperText="The link cannot be redeemed after this time."
          />
          <NumberInput
            source="maxUses"
            label="Maximum uses"
            defaultValue={1}
            min={1}
            max={100}
            validate={required()}
            helperText="Use 1 for a link intended for one customer."
          />
          <TextInput
            source="campaign"
            label="Campaign / source (optional)"
            helperText="Internal tracking label, for example: 2026 wedding pilot."
            fullWidth
          />
          <TextInput
            source="note"
            label="Internal note (optional)"
            helperText="Internal context, for example: Customer paid by bank transfer."
            multiline
            fullWidth
          />
          {isLoading && <p>Creating…</p>}
        </SimpleForm>
      )}
    </Create>
  );
};
